'use client'
import LetterDetailContent from '@/components/letter/LetterDetailContent'
import { useLetterDetail } from '@/hooks/letter/useLetterDetail'
import { LetterItem, LetterMessageItem } from './LetterItem'
import { useLetterReadStatus } from '@/hooks/letter/useLetterReadStatus'
import { usePersonalList } from '@/hooks/letter/usePersonalList'
import { useSystemList } from '@/hooks/letter/useSystemList'
import { useEngagementList } from '@/hooks/letter/useEngagementList'
import useInfiniteScroll from '@/hooks/useInfiniteScroll'
import { NavBackOutlinedMobileIcon, NotificationFeedbackIcon } from '@/icons'
import { readAllMessages } from '@/apis/letter'
import LoadingImg from '@/icons/LoadingImg'
import clsx from 'clsx'
import { message } from 'antd'
import { useCallback, useEffect, useMemo, useState } from 'react'
import FeedbackModal from '@/components/modal/FeedbackModal'
import useI18n from '@/hooks/useI18n'
import { SystemMessageRes } from '@/types/letter'

const tabList = ['System', 'Personal', 'Engagement']

const TAB_TYPE_MAP: Record<string, ('system' | 'user' | 'engagement')[]> = {
  System: ['system'],
  Personal: ['user'],
  Engagement: ['engagement'],
}

export default function LetterPage() {
  const { t } = useI18n()
  const [tab, setTab] = useState<'System' | 'Personal' | 'Engagement'>('System')
  const [isDetail, setIsDetail] = useState(false)
  const [messgeId, setMessgeId] = useState('')
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false)
  const [isReadingAll, setIsReadingAll] = useState(false)
  const { data: readStatus, mutate: mutateReadStatus } = useLetterReadStatus()
  const {
    data: systemList,
    hasLoaded: hasLoadedSystemList,
    mutate: mutateSystemList,
    loadMore: loadMoreSystemList,
    isLoading: isLoadingSystemList,
  } = useSystemList(tab)
  const {
    data: personalList,
    hasLoaded: hasLoadedPersonalList,
    mutate: mutatePersonalList,
    loadMore: loadMorePersonalList,
    isLoading: isLoadingPersonalList,
  } = usePersonalList(tab)

  const {
    data: engagementList,
    hasLoaded: hasLoadedEngagementList,
    mutate: mutateEngagementList,
    loadMore: loadMoreEngagementList,
    isLoading: isLoadingEngagementList,
    getUnreadCount,
  } = useEngagementList(tab, readStatus)
  const { data: detailData, mutate: mutateDetail } = useLetterDetail(
    tab as 'System' | 'Personal',
    messgeId as string
  )
  const { touchBottom, loadMoreRef } = useInfiniteScroll()

  const messageList = useMemo(() => {
    if (tab === 'Engagement') {
      return Array.isArray(engagementList)
        ? engagementList.filter((item) => item != null && item.message_id)
        : []
    }
    return tab === 'System' ? systemList : personalList
  }, [tab, systemList, personalList, engagementList])

  const isLoading = useMemo(() => {
    return (
      (isLoadingSystemList && tab === 'System') ||
      (isLoadingPersonalList && tab === 'Personal') ||
      (isLoadingEngagementList && tab === 'Engagement')
    )
  }, [isLoadingSystemList, isLoadingPersonalList, isLoadingEngagementList, tab])
  const hasLoadedCurrentTab = useMemo(() => {
    if (tab === 'System') return hasLoadedSystemList
    if (tab === 'Personal') return hasLoadedPersonalList
    return hasLoadedEngagementList
  }, [tab, hasLoadedSystemList, hasLoadedPersonalList, hasLoadedEngagementList])

  const handleOptimisticRead = (
    messageId: string,
    currentTab: 'System' | 'Personal'
  ) => {
    const now = Math.floor(Date.now() / 1000)
    let hasUnreadInTab: boolean | null = null
    const getHasUnread = (pages: Array<SystemMessageRes | undefined>) =>
      pages.some((page) => page?.list?.some((item) => item?.read_time === 0))
    const updatePages = (
      pages: Array<SystemMessageRes | undefined>
    ): Array<SystemMessageRes | undefined> => {
      return pages.map((page) => {
        if (!page) return page
        return {
          ...page,
          list:
            page.list?.map((item) =>
              item?.message_id === messageId
                ? { ...item, read_time: now }
                : item
            ) || [],
        }
      })
    }
    const applyUpdate = (current?: Array<SystemMessageRes | undefined>) => {
      const next = updatePages(current || [])
      if (current) {
        hasUnreadInTab = getHasUnread(next)
      }
      return next
    }

    if (currentTab === 'System') {
      mutateSystemList((current) => applyUpdate(current), {
        optimisticData: (current) => applyUpdate(current),
        rollbackOnError: true,
        revalidate: false,
      })
    } else {
      mutatePersonalList((current) => applyUpdate(current), {
        optimisticData: (current) => applyUpdate(current),
        rollbackOnError: true,
        revalidate: false,
      })
    }

    mutateReadStatus(
      (current) => {
        if (!current) return current
        const unreadForTab =
          hasUnreadInTab ??
          (currentTab === 'System'
            ? current.unread_system_messages
            : current.unread_user_messages)
        const next = {
          ...current,
          unread_system_messages:
            currentTab === 'System'
              ? unreadForTab
              : current.unread_system_messages,
          unread_user_messages:
            currentTab === 'Personal'
              ? unreadForTab
              : current.unread_user_messages,
        }
        return {
          ...next,
          unread_messages: Boolean(
            next.unread_system_messages ||
            next.unread_user_messages ||
            next.unread_engagement_messages
          ),
        }
      },
      {
        optimisticData: (current) => {
          if (!current) return current
          const unreadForTab =
            hasUnreadInTab ??
            (currentTab === 'System'
              ? current.unread_system_messages
              : current.unread_user_messages)
          const next = {
            ...current,
            unread_system_messages:
              currentTab === 'System'
                ? unreadForTab
                : current.unread_system_messages,
            unread_user_messages:
              currentTab === 'Personal'
                ? unreadForTab
                : current.unread_user_messages,
          }
          return {
            ...next,
            unread_messages: Boolean(
              next.unread_system_messages ||
              next.unread_user_messages ||
              next.unread_engagement_messages
            ),
          }
        },
        rollbackOnError: true,
        revalidate: false,
      }
    )
  }

  const hasUnreadForTab = useCallback(
    (currentTab: string) => {
      if (!readStatus) return false
      if (currentTab === 'System') return readStatus.unread_system_messages
      if (currentTab === 'Personal') return readStatus.unread_user_messages
      if (currentTab === 'Engagement')
        return readStatus.unread_engagement_messages
      return false
    },
    [readStatus]
  )

  const handleReadAll = useCallback(async () => {
    if (isReadingAll) return
    if (readStatus && !hasUnreadForTab(tab)) {
      message.info(t('No unread messages'))
      return
    }
    setIsReadingAll(true)
    try {
      await readAllMessages(TAB_TYPE_MAP[tab])
      message.success(t('All messages marked as read'))
      mutateReadStatus()
      if (tab === 'System') mutateSystemList()
      else if (tab === 'Personal') mutatePersonalList()
      else if (tab === 'Engagement') mutateEngagementList()
    } catch {
      message.error(t('Operation Failed'))
    } finally {
      setIsReadingAll(false)
    }
  }, [
    isReadingAll,
    readStatus,
    hasUnreadForTab,
    tab,
    mutateReadStatus,
    mutateSystemList,
    mutatePersonalList,
    mutateEngagementList,
    t,
  ])

  useEffect(() => {
    if (isLoading) return
    if (touchBottom) {
      if (tab === 'System') {
        loadMoreSystemList()
      } else if (tab === 'Personal') {
        loadMorePersonalList()
      } else if (tab === 'Engagement') {
        loadMoreEngagementList()
      }
    }
  }, [
    touchBottom,
    loadMoreSystemList,
    loadMorePersonalList,
    loadMoreEngagementList,
    tab,
    isLoading,
  ])

  return (
    <>
      <div className="transition-scrollbar-slim h-full overflow-y-auto [scrollbar-gutter:stable]">
        {isDetail ? (
          <div className="relative">
            <div className="relative flex min-h-[56px] w-full items-center px-2.5 py-4">
              <div className="absolute left-1.5 cursor-pointer">
                <NavBackOutlinedMobileIcon onClick={() => setIsDetail(false)} />
              </div>
              <button
                type="button"
                onClick={() => setFeedbackModalOpen(true)}
                className="absolute right-3 inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/10 px-2.5 py-1 text-xs text-white/80"
              >
                <NotificationFeedbackIcon width={16} height={16} />
                <span>{t('Feedback')}</span>
              </button>
            </div>
            <LetterDetailContent detail={detailData} />
          </div>
        ) : (
          <>
            <div className="relative flex w-full items-center px-2.5 py-4">
              <div className="mx-auto text-base text-white">
                {t('Notification')}
              </div>
              <button
                type="button"
                onClick={handleReadAll}
                disabled={isReadingAll}
                className="absolute right-2.5 cursor-pointer border-none bg-transparent text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t('All Read')}
              </button>
            </div>
            <div className="mb-5 flex items-center justify-between gap-2">
              {tabList.map((item) => (
                <div
                  key={item}
                  onClick={() =>
                    setTab(item as 'System' | 'Personal' | 'Engagement')
                  }
                  className="relative flex h-[44px] flex-1 cursor-pointer items-center justify-center"
                >
                  <div className="relative flex items-center">
                    <div
                      className={clsx(
                        tab === item ? 'text-white' : 'text-white/50',
                        'text-sm'
                      )}
                    >
                      {t(item)}
                    </div>
                    {readStatus?.unread_user_messages &&
                      item === 'Personal' && (
                        <div className="absolute -right-2 top-0 h-1.5 w-1.5 rounded-full bg-[#ff3b30]" />
                      )}
                    {readStatus?.unread_system_messages &&
                      item === 'System' && (
                        <div className="absolute -right-2 top-0 h-1.5 w-1.5 rounded-full bg-[#ff3b30]" />
                      )}
                    {readStatus?.unread_engagement_messages &&
                      item === 'Engagement' && (
                        <div className="absolute -right-2 top-0 h-1.5 w-1.5 rounded-full bg-[#ff3b30]" />
                      )}
                  </div>
                  {tab === item && (
                    <div className="absolute bottom-0 h-[2px] w-[16px] bg-[#ff3b30]"></div>
                  )}
                </div>
              ))}
            </div>
            {!hasLoadedCurrentTab ? (
              <div className="mt-[30%] flex items-center justify-center">
                <LoadingImg className="scale-[.8]" />
              </div>
            ) : messageList.length > 0 ? (
              messageList.map((item) => (
                <LetterItem
                  key={item?.message_id}
                  item={item as LetterMessageItem}
                  tab={tab}
                  handlePress={
                    tab === 'Engagement'
                      ? undefined
                      : () => {
                          setIsDetail(true)
                          setMessgeId(item.message_id)
                          handleOptimisticRead(
                            item.message_id,
                            tab as 'System' | 'Personal'
                          )
                        }
                  }
                />
              ))
            ) : (
              <div className="mt-[30%] flex flex-col items-center justify-center">
                <img src="/empty-letter.png" alt="" width={150} height={90} />
                <span className="mt-[36px] text-sm text-white/50">
                  {t("You Don't Have Any Notifications")}
                </span>
              </div>
            )}
            <div ref={loadMoreRef} className="h-1" />
          </>
        )}
      </div>
      <FeedbackModal
        open={feedbackModalOpen}
        onCancel={() => setFeedbackModalOpen(false)}
      />
    </>
  )
}
